import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FlaskConical, Cpu, TestTube, Users, AlertTriangle, Award, Package, Clock,
  RefreshCw, TrendingUp, TrendingDown, Minus, MapPin, X, Activity,
  BarChart2, CheckCircle2, Trophy,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
} from 'recharts';
import api from '@/lib/api';
import PageHeader    from '@/components/ui/PageHeader';
import FilterBar     from '@/components/ui/FilterBar';
import AlertBanner   from '@/components/ui/AlertBanner';
import PillBadge     from '@/components/ui/PillBadge';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import { DashboardProvider, useDashboard } from '@/context/DashboardContext';
import DashboardChart from '@/components/ui/DashboardChart';
import KPIPinnedCards, { KPIEntry } from '@/components/ui/KPIPinnedCards';
import { format } from 'date-fns';

// ── Brand colours (light-mode design system) ──────────────────────────────────
const GOLD   = '#b07d1a';
const RED    = '#DC2626';
const GREEN  = '#16A34A';
const ORANGE = '#D97706';
const BLUE   = '#2563EB';
const PURPLE = '#7C3AED';
const CYAN   = '#0891B2';

// ── Animated counter ──────────────────────────────────────────────────────────
function AnimatedNumber({ value }: { value: number | string }) {
  const [display, setDisplay] = useState(0);
  const num = typeof value === 'number' ? value : 0;
  const isN = typeof value === 'number';
  useEffect(() => {
    if (!isN) return;
    const dur = 700, start = performance.now();
    const raf = (ts: number) => {
      const p = Math.min((ts - start) / dur, 1);
      setDisplay(Math.round(num * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
  }, [num, isN]);
  return <>{isN ? display.toLocaleString() : value}</>;
}

// ── Mini sparkline ────────────────────────────────────────────────────────────
function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data?.length) return null;
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
      {data.slice(-8).map((v, i, arr) => (
        <div key={i} style={{
          flex: 1, height: `${Math.max(3, (v / max) * 24)}px`,
          background: color, borderRadius: '2px 2px 0 0',
          opacity: i === arr.length - 1 ? 1 : 0.28,
        }} />
      ))}
    </div>
  );
}

// ── Cert Health Donut with SVG centre label ───────────────────────────────────
function CertDonut({ valid, renewalDue, expired }: { valid: number; renewalDue: number; expired: number }) {
  const total = valid + renewalDue + expired;
  const pct   = total > 0 ? Math.round((valid / total) * 100) : 0;
  const data  = [
    { name: 'Valid',       value: valid,      color: GREEN  },
    { name: 'Renewal Due', value: renewalDue, color: ORANGE },
    { name: 'Expired',     value: expired,    color: RED    },
  ];
  const CentreLabel = () => (
    <g>
      <text x="50%" y="46%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: 'DM Mono, monospace', fontSize: 22, fontWeight: 700, fill: '#1a1a2e' }}>
        {pct}%
      </text>
      <text x="50%" y="61%" textAnchor="middle" dominantBaseline="middle"
        style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, fill: '#5a5a6e' }}>
        Valid
      </text>
    </g>
  );
  return (
    <>
      <div style={{ height: 185 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={56} outerRadius={74}
              paddingAngle={3} dataKey="value" strokeWidth={0}>
              {data.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Pie>
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <CentreLabel />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-center gap-4 mt-1">
        {data.map((d) => (
          <div key={d.name} className="flex items-center gap-1.5">
            <div style={{ width: 8, height: 8, borderRadius: 2, background: d.color }} />
            <span className="text-[10.5px] text-text-muted">
              {d.name} <span className="font-mono font-semibold text-text">{d.value}</span>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Analytics summary strip ───────────────────────────────────────────────────
interface StatChipProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub: string;
  color: string;
  delay?: number;
}
function StatChip({ icon, label, value, sub, color, delay = 0 }: StatChipProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      whileHover={{ y: -2, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}
      style={{
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 4,
        padding: '18px 20px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'relative',
        overflow: 'hidden',
        cursor: 'default',
      }}
    >
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color}, ${color}66)`,
      }} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 10.5, fontWeight: 600, color: '#5a5a6e', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8, fontFamily: '"DM Mono", monospace' }}>{label}</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <span style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: 36, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>
              <AnimatedNumber value={typeof value === 'number' ? value : 0} />
            </span>
            {typeof value === 'string' && <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{value}</span>}
          </div>
          <p style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>{sub}</p>
        </div>
        <div style={{
          width: 48, height: 48, borderRadius: 4,
          background: `${color}10`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color, flexShrink: 0,
        }}>
          {React.cloneElement(icon as React.ReactElement, { size: 22 })}
        </div>
      </div>
    </motion.div>
  );
}

// ── #15 Range picker ──────────────────────────────────────────────────────────
function RangePicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const ranges = ['3m', '6m', '12m'];
  return (
    <div style={{ display: 'flex', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden' }}>
      {ranges.map((r) => (
        <button key={r} onClick={() => onChange(r)} style={{
          padding: '3px 10px', fontSize: 11, fontWeight: value === r ? 700 : 500,
          background: value === r ? '#b07d1a' : '#fff', color: value === r ? '#fff' : '#5a5a6e',
          border: 'none', cursor: 'pointer', borderRight: r !== '12m' ? '1px solid rgba(0,0,0,0.1)' : 'none',
          textTransform: 'uppercase',
        }}>
          {r}
        </button>
      ))}
    </div>
  );
}

// ── Inner page ────────────────────────────────────────────────────────────────
function DashboardInner() {
  const navigate = useNavigate();
  const { crossFilter, setCrossFilter } = useDashboard();
  const [dateRange,   setDateRange  ] = useState('12m');
  const [labId,       setLabId      ] = useState('');
  const [chartRange,  setChartRange ] = useState<'3m' | '6m' | '12m'>('6m');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const filters = { range: dateRange, ...(labId ? { labId } : {}) };
  const chartFilters = { ...filters, range: chartRange };

  const refresh = useCallback(() => {
    setLastUpdated(new Date());
    window.location.reload();
  }, []);

  const { data: kpis,        isLoading: kpisLoading    } = useQuery({
    queryKey: ['dashboard', 'kpis', filters],
    queryFn:  () => api.get('/dashboard/kpis', { params: filters }).then((r) => r.data.data),
    staleTime: 60000,
  });
  // #14 — Previous period KPIs for MoM delta
  const { data: prevKpis } = useQuery({
    queryKey: ['dashboard-prev', { labId }],
    queryFn:  () => api.get('/dashboard/kpis', { params: { ...(labId && { labId }), period: 'prev_month' } }).then((r) => r.data.data),
    staleTime: 300000,
  });
  const { data: trends,      isLoading: trendsLoading  } = useQuery({
    queryKey: ['dashboard', 'trends', chartFilters],
    queryFn:  () => api.get('/dashboard/trends', { params: chartFilters }).then((r) => r.data.data),
    staleTime: 300000,
  });
  const { data: upcoming,    isLoading: upcomingLoading } = useQuery({
    queryKey: ['dashboard', 'upcoming', filters],
    queryFn:  () => api.get('/dashboard/upcoming', { params: filters }).then((r) => r.data.data),
  });
  const { data: equipmentDist } = useQuery({
    queryKey: ['dashboard', 'equipment-dist', filters],
    queryFn:  () => api.get('/dashboard/equipment-dist', { params: filters }).then((r) => r.data.data),
  });
  const { data: certHealth } = useQuery({
    queryKey: ['dashboard', 'cert-health', filters],
    queryFn:  () => api.get('/dashboard/cert-health', { params: filters }).then((r) => r.data.data),
  });
  const { data: partnerMap } = useQuery({
    queryKey: ['dashboard', 'partner-map', filters],
    queryFn:  () => api.get('/dashboard/partner-map', { params: filters }).then((r) => r.data.data),
  });
  const { data: complianceRadar } = useQuery({
    queryKey: ['dashboard', 'compliance-radar', filters],
    queryFn:  () => api.get('/dashboard/compliance-radar', { params: filters }).then((r) => r.data.data),
  });
  const { data: labPerformance } = useQuery({
    queryKey: ['dashboard', 'lab-performance', filters],
    queryFn:  () => api.get('/dashboard/lab-performance', { params: filters }).then((r) => r.data.data),
  });

  // ── KPI entries ──────────────────────────────────────────────────────────────
  const kpiEntries: KPIEntry[] = kpis ? [
    { key: 'activeLabs',       label: 'Active Labs',       value: kpis.activeLabs?.value       ?? 0, previousValue: prevKpis?.activeLabs?.value,       trend: Math.abs(kpis.activeLabs?.trend       ?? 0), trendDir: kpis.activeLabs?.trendDir,       sparkline: kpis.activeLabs?.sparkline,       color: GOLD,      icon: <FlaskConical size={13} color={GOLD}      />, route: '/registry'                          },
    { key: 'equipmentTypes',   label: 'Equipment Types',   value: kpis.equipmentTypes?.value   ?? 0, previousValue: prevKpis?.equipmentTypes?.value,   trend: Math.abs(kpis.equipmentTypes?.trend   ?? 0), trendDir: kpis.equipmentTypes?.trendDir,   sparkline: kpis.equipmentTypes?.sparkline,   color: BLUE,      icon: <Cpu           size={13} color={BLUE}      />, route: '/equipment'                         },
    { key: 'testCapabilities', label: 'Test Capabilities', value: kpis.testCapabilities?.value ?? 0, previousValue: prevKpis?.testCapabilities?.value, trend: Math.abs(kpis.testCapabilities?.trend ?? 0), trendDir: kpis.testCapabilities?.trendDir, sparkline: kpis.testCapabilities?.sparkline, color: PURPLE,    icon: <TestTube      size={13} color={PURPLE}    />, route: '/requests'                          },
    { key: 'partnerLabs',      label: 'Partner Labs',      value: kpis.partnerLabs?.value      ?? 0, previousValue: prevKpis?.partnerLabs?.value,      trend: Math.abs(kpis.partnerLabs?.trend      ?? 0), trendDir: kpis.partnerLabs?.trendDir,      sparkline: kpis.partnerLabs?.sparkline,      color: CYAN,      icon: <Users         size={13} color={CYAN}      />, route: '/partners'                          },
    { key: 'openCapas',        label: 'Open CAPAs',        value: kpis.openCapas?.value        ?? 0, previousValue: prevKpis?.openCapas?.value,        trend: Math.abs(kpis.openCapas?.trend        ?? 0), trendDir: kpis.openCapas?.trendDir,        sparkline: kpis.openCapas?.sparkline,        color: RED,       icon: <AlertTriangle size={13} color={RED}       />, route: '/capa?status=open'                  },
    { key: 'certAlerts',       label: 'Cert Alerts',       value: kpis.certAlerts?.value       ?? 0, previousValue: prevKpis?.certAlerts?.value,       trend: Math.abs(kpis.certAlerts?.trend       ?? 0), trendDir: kpis.certAlerts?.trendDir,       sparkline: kpis.certAlerts?.sparkline,       color: ORANGE,    icon: <Award         size={13} color={ORANGE}    />, route: '/certifications?status=renewal_due' },
    { key: 'batchesOnHold',    label: 'Batches on Hold',   value: kpis.batchesOnHold?.value    ?? 0, previousValue: prevKpis?.batchesOnHold?.value,    trend: Math.abs(kpis.batchesOnHold?.trend    ?? 0), trendDir: kpis.batchesOnHold?.trendDir,    sparkline: kpis.batchesOnHold?.sparkline,    color: ORANGE,    icon: <Package       size={13} color={ORANGE}    />, route: '/bmr?status=qc_hold'               },
    { key: 'pendingRequests',  label: 'Pending Requests',  value: kpis.pendingRequests?.value  ?? 0, previousValue: prevKpis?.pendingRequests?.value,  trend: Math.abs(kpis.pendingRequests?.trend  ?? 0), trendDir: kpis.pendingRequests?.trendDir,  sparkline: kpis.pendingRequests?.sparkline,  color: '#6B7280', icon: <Clock         size={13} color="#6B7280"    />, route: '/requests?status=pending'           },
  ] : [];

  // ── Normalised trend data ────────────────────────────────────────────────────
  const safeTrends = Array.isArray(trends) ? trends : [];
  const trendsData = safeTrends.map((t: Record<string, unknown>) => ({
    ...t, label: String(t.label ?? t.month ?? ''),
  }));

  // ── queryFn for drill-down ────────────────────────────────────────────────────
  const trendsQueryFn = useCallback(
    (params: Record<string, unknown>) =>
      api.get('/dashboard/trends', { params: { ...chartFilters, ...params } }).then((r) => {
        const raw = r.data.data;
        return (Array.isArray(raw) ? raw as Record<string, unknown>[] : []).map((t) => ({ ...t, label: String(t.label ?? t.month ?? '') }));
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartRange, labId]
  );

  // ── Equipment dist → DashboardChart format ────────────────────────────────────
  const eqData = (Array.isArray(equipmentDist) ? equipmentDist : []).map((e: { type: string; count: number }) => ({
    type: e.type, count: e.count,
  }));

  // ── Analytics summary derived metrics ────────────────────────────────────────
  const totalCapas    = safeTrends.reduce((s: number, t: Record<string, unknown>) => s + (Number(t.openCapas ?? 0) + Number(t.closedCapas ?? 0)), 0);
  const closedCapas   = safeTrends.reduce((s: number, t: Record<string, unknown>) => s + Number(t.closedCapas ?? 0), 0);
  const closureRate   = totalCapas > 0 ? Math.round((closedCapas / totalCapas) * 100) : 0;
  const certValidPct  = certHealth ? (
    certHealth.valid + certHealth.renewalDue + certHealth.expired > 0
      ? Math.round((certHealth.valid / (certHealth.valid + certHealth.renewalDue + certHealth.expired)) * 100)
      : 0
  ) : 0;
  const totalBatches  = safeTrends.reduce((s: number, t: Record<string, unknown>) => s + Number(t.batchReleased ?? 0) + Number(t.batchHold ?? 0), 0);
  const relBatches    = safeTrends.reduce((s: number, t: Record<string, unknown>) => s + Number(t.batchReleased ?? 0), 0);
  const releaseRate   = totalBatches > 0 ? Math.round((relBatches / totalBatches) * 100) : 0;

  // ── Partner map city coords ───────────────────────────────────────────────────
  const cityCoords: Record<string, [number, number]> = {
    Mumbai: [28, 58], Pune: [30, 62], Bangalore: [38, 74], Hyderabad: [42, 64],
    Chennai: [47, 78], Delhi: [43, 28], Ahmedabad: [24, 47], Kolkata: [65, 52],
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Page header ────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '20px 24px',
        background: 'rgba(255,255,255,0.75)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderRadius: 4,
        border: '1px solid rgba(0,0,0,0.08)',
        borderLeft: `3px solid ${GOLD}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div>
          <h1 style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: 28, fontWeight: 700, color: '#1a1a2e', margin: 0, lineHeight: 1.1 }}>
            Executive Dashboard
          </h1>
          <p style={{ fontSize: 11.5, color: '#5a5a6e', marginTop: 4, fontFamily: '"DM Mono", monospace', letterSpacing: '0.04em' }}>
            Quality · Lab · Partner Intelligence · Updated {format(lastUpdated, 'HH:mm')}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <RangePicker value={chartRange} onChange={(v) => setChartRange(v as '3m' | '6m' | '12m')} />
          <button onClick={refresh} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 2,
            background: 'transparent', border: `1px solid rgba(0,0,0,0.1)`,
            color: '#5a5a6e', fontSize: 12, fontWeight: 500, cursor: 'pointer',
            transition: 'all 0.15s',
          }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)'; e.currentTarget.style.color = '#5a5a6e'; }}
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <FilterBar
        labId={labId}         onLabChange={(id) => setLabId(id)}
        dateRange={dateRange} onDateRangeChange={(r) => setDateRange(r)}
        defaultDateRange="12m"
      />

      {/* ── Global cross-filter banner ──────────────────────────────────────── */}
      <AnimatePresence>
        {crossFilter && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 16px', background: '#F0F9FF', border: '1px solid #BAE6FD',
              borderRadius: 4, fontSize: 12,
            }}
          >
            <Activity size={13} style={{ color: BLUE, flexShrink: 0 }} />
            <span style={{ color: '#0369A1', fontWeight: 600 }}>Cross-filter active:</span>
            <span style={{ color: '#1a1a2e' }}>{crossFilter.label}</span>
            <span style={{ fontSize: 10.5, color: '#5a5a6e', marginLeft: 4 }}>
              — all charts are filtered by this selection
            </span>
            <button onClick={() => setCrossFilter(null)} style={{
              marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
              color: '#94A3B8', display: 'flex', alignItems: 'center',
            }}>
              <X size={13} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Cert alert ─────────────────────────────────────────────────────── */}
      {certHealth?.expired > 0 && (
        <AlertBanner
          variant="red"
          title={`${certHealth.expired} certification${certHealth.expired > 1 ? 's' : ''} expired`}
          message="Immediate action required — expired certifications may affect lab operations."
          onClick={() => navigate('/certifications?status=expired')}
        />
      )}

      {/* ── Analytics summary strip ─────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        <StatChip icon={<BarChart2 size={16} />}    label="CAPA Closure Rate" value={closureRate}  sub="% closed this period" color={closureRate >= 70 ? GREEN : closureRate >= 40 ? ORANGE : RED} delay={0} />
        <StatChip icon={<CheckCircle2 size={16} />} label="Cert Validity"     value={certValidPct} sub="% certs in-date"       color={certValidPct >= 80 ? GREEN : certValidPct >= 60 ? ORANGE : RED} delay={0.05} />
        <StatChip icon={<Package size={16} />}      label="Batch Release Rate" value={releaseRate} sub="% batches released"    color={releaseRate >= 80 ? GREEN : releaseRate >= 60 ? ORANGE : RED} delay={0.10} />
      </div>

      {/* ── KPI pinnable cards ──────────────────────────────────────────────── */}
      <KPIPinnedCards
        entries={kpiEntries}
        loading={kpisLoading}
        onNavigate={(route) => navigate(route)}
      />

      {/* ── Row 1: CAPA Trend + Cert Health ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <div>
          {trendsLoading
            ? <ChartSkeleton height={280} />
            : (
              <DashboardChart
                chartId="capa-trend"
                title="CAPA Monthly Trend"
                subtitle="Open / Closed / Overdue — click bar for cross-filter · drill-down · forecast · export"
                data={trendsData}
                queryFn={trendsQueryFn}
                baseParams={chartFilters}
                xDataKey="label"
                height={280}
                defaultChartType="bar"
                series={[
                  { key: 'openCapas',    name: 'Open',    color: RED    },
                  { key: 'closedCapas',  name: 'Closed',  color: GREEN  },
                  { key: 'overdueCapas', name: 'Overdue', color: ORANGE },
                ]}
                config={{
                  enableCrossFilter: true,
                  enableForecast:    true,
                  enableBenchmark:   true,
                  enableBrush:       true,
                  enableAnnotations: true,
                  enableDrillDown:   true,
                  benchmarkValue:    5,
                  benchmarkLabel:    'Target ≤5 Open',
                  linkedPage:        '/capa',
                  drillLevels: [
                    { label: 'Weekly', paramKey: 'month' },
                    { label: 'Daily',  paramKey: 'week'  },
                  ],
                }}
                onBarClick={() => navigate('/capa')}
              />
            )}
        </div>

        {/* Cert Health Donut */}
        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 4, padding: 20,
          border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, fontSize: 18, color: '#1a1a2e', margin: 0, lineHeight: 1.1 }}>Certification Health</h3>
          <p style={{ fontSize: 11.5, color: '#5a5a6e', marginTop: 2, marginBottom: 12 }}>Status breakdown · click to navigate</p>
          {certHealth ? (
            <div onClick={() => navigate('/certifications?status=expired')} style={{ cursor: 'pointer' }}>
              <CertDonut
                valid={certHealth.valid      ?? 0}
                renewalDue={certHealth.renewalDue ?? 0}
                expired={certHealth.expired    ?? 0}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-44">
              <div className="w-6 h-6 rounded-full border-2 border-border border-t-teal animate-spin" />
            </div>
          )}
          {/* Renewal due alert */}
          {certHealth?.renewalDue > 0 && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer"
              onClick={() => navigate('/certifications?status=renewal_due')}>
              <AlertTriangle size={12} style={{ color: ORANGE, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#92400E' }}>
                <strong>{certHealth.renewalDue}</strong> cert{certHealth.renewalDue > 1 ? 's' : ''} due for renewal
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ── Row 2: Equipment + Batch Output ───────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 20 }}>
        {/* Equipment Distribution — DashboardChart with full toolbar */}
        <DashboardChart
          chartId="equipment-dist"
          title="Equipment Distribution"
          subtitle="Instrument types — sort, top-N, export, fullscreen"
          data={eqData}
          xDataKey="type"
          height={240}
          defaultChartType="bar"
          series={[{ key: 'count', name: 'Equipment Count', color: BLUE }]}
          config={{
            enableCrossFilter: true,
            enableForecast:    false,
            enableBenchmark:   false,
            enableBrush:       false,
            enableAnnotations: false,
            linkedPage:        '/equipment',
          }}
          onBarClick={() => navigate('/equipment')}
        />

        {/* Batch Output — DashboardChart */}
        <div>
          {trendsLoading
            ? <ChartSkeleton height={240} />
            : (
              <DashboardChart
                chartId="batch-output"
                title="Batch Monthly Output"
                subtitle="Released / On Hold — forecast · benchmark · brush · export"
                data={trendsData}
                queryFn={trendsQueryFn}
                baseParams={chartFilters}
                xDataKey="label"
                height={240}
                series={[
                  { key: 'batchReleased', name: 'Released', color: GREEN  },
                  { key: 'batchHold',     name: 'On Hold',  color: ORANGE },
                ]}
                config={{
                  enableCrossFilter: true,
                  enableForecast:    true,
                  enableBenchmark:   true,
                  enableBrush:       true,
                  enableAnnotations: true,
                  benchmarkValue:    10,
                  benchmarkLabel:    'Hold threshold',
                  linkedPage:        '/bmr',
                }}
                onBarClick={() => navigate('/bmr')}
              />
            )}
        </div>
      </div>

      {/* ── Row 3: Upcoming Events + Partner Map ──────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>

        {/* Upcoming Events */}
        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 4, padding: 20,
          border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, fontSize: 18, color: '#1a1a2e', margin: 0, lineHeight: 1.1 }}>Upcoming Events</h3>
              <p style={{ fontSize: 11.5, color: '#5a5a6e', marginTop: 2 }}>Next 30 days · sorted by urgency</p>
            </div>
            {upcoming?.length > 0 && (
              <span className="text-xs font-mono text-text-dim">{upcoming.length} events</span>
            )}
          </div>

          {upcomingLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-card2 animate-pulse" />
              ))}
            </div>
          ) : !upcoming?.length ? (
            <p className="text-sm text-text-muted py-6 text-center">No upcoming events.</p>
          ) : (
            <div className="space-y-1.5">
              {upcoming.slice(0, 8).map((e: {
                id: string; type: string; title: string; subtitle: string;
                date: string; urgency: string; daysLeft?: number;
              }, i: number) => {
                const uColor = ({ critical: RED, high: ORANGE, medium: BLUE, low: GREEN } as Record<string, string>)[e.urgency] || GOLD;
                return (
                  <motion.div
                    key={e.id}
                    initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.035 }}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-bg hover:bg-card2 transition-colors cursor-pointer border border-transparent hover:border-border"
                    onClick={() => {
                      const statusMap: Record<string, string> = {
                        capa: '/capa?status=open',
                        audit: '/audits',
                        certification: '/certifications?status=renewal_due',
                        equipment: '/equipment',
                        batch: '/bmr?status=qc_hold',
                        request: '/requests?status=pending',
                        sop: '/sop',
                      };
                      navigate(statusMap[e.type] || '/dashboard');
                    }}
                  >
                    <div style={{ width: 3, height: 32, borderRadius: 2, background: uColor, flexShrink: 0 }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-text truncate">{e.title}</p>
                      <p className="text-[10px] text-text-dim">{e.subtitle}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] font-mono text-text-muted">{format(new Date(e.date), 'd MMM')}</p>
                      {e.daysLeft !== undefined && (
                        <p className={`text-[9px] font-semibold ${e.urgency === 'critical' ? 'text-risk-high' : 'text-text-dim'}`}>
                          {e.daysLeft === 0 ? 'Today' : `${e.daysLeft}d`}
                        </p>
                      )}
                    </div>
                    <PillBadge value={e.urgency} size="xs" />
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>

        {/* Partner Map */}
        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 4, padding: 20,
          border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, fontSize: 18, color: '#1a1a2e', margin: '0 0 12px', lineHeight: 1.1 }}>Partner Lab Map</h3>
          <div className="relative bg-card2 rounded-xl overflow-hidden" style={{ height: 210 }}>
            <svg viewBox="0 0 400 420" className="w-full h-full opacity-20">
              <path d="M200,20 L240,35 L270,50 L300,75 L320,100 L340,130 L355,160 L360,195 L355,225 L340,255 L315,280 L300,310 L280,335 L255,355 L230,370 L200,380 L170,370 L145,355 L120,335 L100,310 L85,280 L60,255 L45,225 L40,195 L45,160 L60,130 L80,100 L100,75 L130,50 L160,35 Z"
                fill="none" stroke={GOLD} strokeWidth="1.5" />
            </svg>
            {(Array.isArray(partnerMap) ? partnerMap : []).map((lab: { id: string; name: string; city: string; riskScore: number }) => {
              const [cx, cy] = cityCoords[lab.city] || [50, 50];
              const color = lab.riskScore < 40 ? GREEN : lab.riskScore < 70 ? ORANGE : RED;
              return (
                <div key={lab.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  style={{ left: `${cx}%`, top: `${cy}%` }}
                  title={`${lab.name} · Risk: ${Math.round(lab.riskScore)}`}
                  onClick={() => navigate('/partners')}>
                  <div className="absolute inset-0 rounded-full animate-ping"
                    style={{ border: `1.5px solid ${color}`, opacity: 0.4 }} />
                  <div className="w-3 h-3 rounded-full relative z-10"
                    style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
                </div>
              );
            })}
            <div className="absolute bottom-2 left-2 right-2 flex items-center gap-3 text-[9px] text-text-dim">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-low" />Low</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-medium" />Medium</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-risk-high" />High</span>
            </div>
          </div>

          <div className="mt-3 space-y-1.5">
            {(Array.isArray(partnerMap) ? partnerMap : []).slice(0, 5).map((lab: { id: string; name: string; city: string; riskScore: number }) => {
              const color = lab.riskScore < 40 ? GREEN : lab.riskScore < 70 ? ORANGE : RED;
              const riskLabel = lab.riskScore < 40 ? 'Low' : lab.riskScore < 70 ? 'Med' : 'High';
              return (
                <div key={lab.id} className="flex justify-between items-center cursor-pointer hover:bg-bg px-1 py-0.5 rounded"
                  onClick={() => navigate('/partners')}>
                  <div className="flex items-center gap-1.5">
                    <MapPin size={10} style={{ color }} />
                    <span className="text-xs text-text truncate max-w-[120px]">{lab.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-text-muted">{Math.round(lab.riskScore)}</span>
                    <span style={{
                      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 100,
                      background: `${color}18`, color, border: `1px solid ${color}30`,
                    }}>{riskLabel}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-text-muted mt-2">{partnerMap?.length || 0} partner labs mapped</p>
        </div>
      </div>

      {/* ── Row 4: Quality Compliance Radar + Lab Performance ────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: 20 }}>

        {/* Quality Compliance Radar */}
        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 4, padding: 20,
          border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, fontSize: 18, color: '#1a1a2e', margin: '0 0 2px', lineHeight: 1.1 }}>Quality Compliance Radar</h3>
          <p style={{ fontSize: 11.5, color: '#5a5a6e', marginTop: 0, marginBottom: 12 }}>5-axis composite · {labId ? 'selected lab' : 'all labs'}</p>
          {complianceRadar ? (
            <div style={{ height: 220 }}>
              <ResponsiveContainer>
                <RadarChart data={Array.isArray(complianceRadar) ? complianceRadar : []} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                  <PolarGrid stroke="rgba(0,0,0,0.08)" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fontSize: 11, fill: '#5a5a6e', fontFamily: '"DM Sans", sans-serif' }} />
                  <PolarRadiusAxis domain={[0, 100]} tick={{ fontSize: 9, fill: '#94A3B8' }} tickCount={4} />
                  <Radar name="Score" dataKey="score" stroke={GOLD} fill={GOLD} fillOpacity={0.18} strokeWidth={2} dot={{ r: 4, fill: GOLD, strokeWidth: 0 }} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)' }}
                    formatter={(v: number) => [`${v}%`, 'Score']}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${GOLD}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}
          {/* Score pills */}
          {Array.isArray(complianceRadar) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
              {complianceRadar.map((d: { dimension: string; score: number }) => (
                <span key={d.dimension} style={{
                  fontSize: 10, padding: '2px 8px', borderRadius: 100,
                  background: d.score >= 80 ? '#F0FDF4' : d.score >= 60 ? '#FFFBEB' : '#FEF2F2',
                  color:      d.score >= 80 ? GREEN    : d.score >= 60 ? ORANGE    : RED,
                  border: `1px solid ${(d.score >= 80 ? GREEN : d.score >= 60 ? ORANGE : RED)}30`,
                  fontWeight: 600,
                }}>
                  {d.dimension}: {d.score}%
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Lab Performance Leaderboard */}
        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          borderRadius: 4, padding: 20,
          border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div>
              <h3 style={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 700, fontSize: 18, color: '#1a1a2e', margin: 0, lineHeight: 1.1 }}>Lab Performance Leaderboard</h3>
              <p style={{ fontSize: 11.5, color: '#5a5a6e', marginTop: 2 }}>Composite score · CAPA · Cert · Audit</p>
            </div>
            <Trophy size={18} style={{ color: GOLD }} />
          </div>
          {Array.isArray(labPerformance) && labPerformance.length > 0 ? (
            <div style={{ height: 248 }}>
              <ResponsiveContainer>
                <BarChart
                  layout="vertical"
                  data={labPerformance}
                  margin={{ top: 4, right: 16, bottom: 4, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="labName" tick={{ fontSize: 10.5, fill: '#5a5a6e', fontFamily: '"DM Sans", sans-serif' }} width={88} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => [`${v}%`]} />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="capaScore"  name="CAPA Closure"   stackId="a" fill={RED}    radius={[0, 0, 0, 0]} maxBarSize={14} />
                  <Bar dataKey="certScore"  name="Cert Compliance" stackId="a" fill={ORANGE} maxBarSize={14} />
                  <Bar dataKey="auditScore" name="Audit Score"     stackId="a" fill={GREEN}  radius={[0, 4, 4, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 248, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${GOLD}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Exported page ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  return (
    <DashboardProvider>
      <DashboardInner />
    </DashboardProvider>
  );
}
